/**
 * MORFUEL — Interactive behaviors
 * Animated mobile menu, scroll reveals, testimonial drag, cart interactions
 */

(function () {
  'use strict';

  // ── Nav scroll behavior ──────────────────────────────────────
  const nav = document.getElementById('main-nav');
  const announcementBar = document.getElementById('announcement-bar');

  // Keep --header-h in sync with the real fixed-header height so the hero
  // always starts below the nav (never clipped by it).
  const siteHeader = document.querySelector('.site-header');

  function updateHeaderHeight() {
    if (!siteHeader) return;
    const h = Math.round(siteHeader.getBoundingClientRect().height);
    if (h > 0) {
      document.documentElement.style.setProperty('--header-h', h + 'px');
    }
  }

  updateHeaderHeight();
  window.addEventListener('resize', updateHeaderHeight, { passive: true });
  window.addEventListener('load', updateHeaderHeight);

  function handleNavScroll() {
    const y = window.scrollY;
    if (y > 10) {
      nav.classList.add('scrolled');
    } else {
      nav.classList.remove('scrolled');
    }
  }

  window.addEventListener('scroll', handleNavScroll, { passive: true });
  handleNavScroll();

  // ── Mobile menu ──────────────────────────────────────────────
  const navToggle = document.getElementById('nav-toggle');
  const navLinks = document.getElementById('nav-links');
  const navOverlay = document.getElementById('nav-overlay');
  let menuOpen = false;

  function openMenu() {
    menuOpen = true;
    nav.classList.add('menu-open');
    navToggle.classList.add('active');
    navToggle.setAttribute('aria-expanded', 'true');
    navLinks.classList.add('open');
    navOverlay.classList.add('visible');
    document.body.style.overflow = 'hidden';
  }

  function closeMenu() {
    menuOpen = false;
    nav.classList.remove('menu-open');
    navToggle.classList.remove('active');
    navToggle.setAttribute('aria-expanded', 'false');
    navLinks.classList.remove('open');
    navOverlay.classList.remove('visible');
    document.body.style.overflow = '';
  }

  if (navToggle && navLinks) {
    navToggle.setAttribute('aria-expanded', 'false');

    navToggle.addEventListener('click', () => {
      if (menuOpen) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    // Close on overlay tap
    if (navOverlay) {
      navOverlay.addEventListener('click', closeMenu);
    }

    // Close on link click (with slight delay for visual feedback)
    navLinks.querySelectorAll('.nav__link, .nav__menu-cta .btn').forEach(link => {
      link.addEventListener('click', () => {
        setTimeout(closeMenu, 150);
      });
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menuOpen) {
        closeMenu();
      }
    });

    // Close menu if viewport resizes past breakpoint (prevents stuck overlay)
    const mql = window.matchMedia('(min-width: 1025px)');
    mql.addEventListener('change', (e) => {
      if (e.matches && menuOpen) {
        closeMenu();
      }
    });
  }

  // ── Scroll reveal (IntersectionObserver) ─────────────────────
  const revealElements = document.querySelectorAll('.reveal, .reveal-stagger');

  if (window.Shopify && window.Shopify.designMode) {
    revealElements.forEach(el => el.classList.add('visible'));
  } else if ('IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.01, rootMargin: '50px' }
    );

    revealElements.forEach(el => revealObserver.observe(el));
  } else {
    revealElements.forEach(el => el.classList.add('visible'));
  }


  // ── Side Cart Drawer Logic ──────────────────────────────────
  const cartBtn = document.getElementById('cart-btn');
  const sideCartDrawer = document.getElementById('side-cart-drawer');
  const sideCartOverlay = document.getElementById('side-cart-overlay');
  const sideCartCloseBtn = document.getElementById('side-cart-close-btn');

  function openSideCart() {
    if (sideCartDrawer) {
      sideCartDrawer.classList.add('active');
      sideCartDrawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
  }

  function closeSideCart() {
    if (sideCartDrawer) {
      sideCartDrawer.classList.remove('active');
      sideCartDrawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  }

  if (cartBtn) {
    cartBtn.addEventListener('click', (e) => {
      if (window.innerWidth <= 768) {
        // On mobile, navigate to the cart page
        window.location.href = '/cart';
        return;
      }
      e.preventDefault();
      openSideCart();
    });
  }

  if (sideCartCloseBtn) {
    sideCartCloseBtn.addEventListener('click', closeSideCart);
  }

  if (sideCartOverlay) {
    sideCartOverlay.addEventListener('click', closeSideCart);
  }

  const shopNowDrawerBtn = document.getElementById('side-cart-shop-now');
  if (shopNowDrawerBtn) {
    shopNowDrawerBtn.addEventListener('click', closeSideCart);
  }

  // Close side cart on ESC key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && sideCartDrawer && sideCartDrawer.classList.contains('active')) {
      closeSideCart();
    }
  });

  // ── Cart count animation & Side Cart trigger ─────────────────
  const cartCount = document.querySelector('.nav__cart-count');
  let count = parseInt(cartCount ? cartCount.textContent : '0', 10) || 0;

  if (cartCount) {
    cartCount.style.transition = 'transform 200ms cubic-bezier(0.25, 1, 0.5, 1)';
  }

  // Intercept Add to Cart forms and buttons to trigger side cart
  document.querySelectorAll('form[action*="/cart/add"], .product-card__add').forEach(formOrBtn => {
    formOrBtn.addEventListener('submit', (e) => {
      // Let standard form proceed or animate + open drawer
      setTimeout(openSideCart, 300);
    });
  });

  // ── Testimonials horizontal drag scroll ──────────────────────
  const track = document.getElementById('testimonials-track');

  if (track) {
    let isDown = false;
    let startX;
    let scrollLeft;

    track.addEventListener('mousedown', (e) => {
      isDown = true;
      track.style.cursor = 'grabbing';
      startX = e.pageX - track.offsetLeft;
      scrollLeft = track.scrollLeft;
    });

    track.addEventListener('mouseleave', () => {
      isDown = false;
      track.style.cursor = '';
    });

    track.addEventListener('mouseup', () => {
      isDown = false;
      track.style.cursor = '';
    });

    track.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - track.offsetLeft;
      const walk = (x - startX) * 1.5;
      track.scrollLeft = scrollLeft - walk;
    });
  }

  // ── Smooth scroll for anchor links ───────────────────────────
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      const id = this.getAttribute('href');
      if (id === '#') return;
      const target = document.querySelector(id);
      if (!target) return;

      e.preventDefault();
      const navHeight = nav ? nav.offsetHeight : 0;
      const top = target.getBoundingClientRect().top + window.scrollY - navHeight - 16;

      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    });
  });

  // ── Newsletter form ──────────────────────────────────────────
  const newsletterForm = document.getElementById('newsletter-form');

  if (newsletterForm) {
    newsletterForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = newsletterForm.querySelector('input');
      const btn = newsletterForm.querySelector('button');

      if (input.value.trim()) {
        const originalText = btn.textContent;
        btn.textContent = '✓ Joined';
        btn.style.background = 'var(--success)';
        input.value = '';
        input.disabled = true;
        btn.disabled = true;

        setTimeout(() => {
          btn.textContent = originalText;
          btn.style.background = '';
          input.disabled = false;
          btn.disabled = false;
        }, 3000);
      }
    });
  }

  // ── Top Sales Banner (Minimalist) ─────────────────────────
  const salesBanner = document.getElementById('sales-banner');
  const bannerCloseBtn = document.getElementById('banner-close-btn');
  const bannerCopyBtn = document.getElementById('banner-copy-btn');

  if (bannerCopyBtn) {
    bannerCopyBtn.addEventListener('click', () => {
      const code = bannerCopyBtn.getAttribute('data-code') || '';
      navigator.clipboard.writeText(code).catch(() => {});

      const originalText = bannerCopyBtn.textContent;
      bannerCopyBtn.textContent = 'COPIED! ✓';
      bannerCopyBtn.style.color = 'var(--success)';
      bannerCopyBtn.style.background = 'var(--surface-container-high)';

      setTimeout(() => {
        bannerCopyBtn.textContent = originalText;
        bannerCopyBtn.style.color = '';
        bannerCopyBtn.style.background = '';
      }, 2000);
    });
  }

  if (bannerCloseBtn && salesBanner) {
    bannerCloseBtn.addEventListener('click', () => {
      salesBanner.style.transition = 'max-height 300ms var(--ease-out-quart), opacity 200ms';
      salesBanner.style.maxHeight = salesBanner.offsetHeight + 'px';
      requestAnimationFrame(() => {
        salesBanner.style.maxHeight = '0px';
        salesBanner.style.opacity = '0';
        salesBanner.style.overflow = 'hidden';
      });

      // Let the hero glide up as the header shrinks — no white gap, no clipping
      document.querySelectorAll('.hero--fullwidth').forEach(hero => {
        hero.style.transition = 'margin-top 300ms var(--ease-out-quart), height 300ms var(--ease-out-quart)';
      });

      const shrink = setInterval(updateHeaderHeight, 16);

      setTimeout(() => {
        clearInterval(shrink);
        salesBanner.style.display = 'none';
        updateHeaderHeight();
      }, 300);
    });
  }

  // ── Parallax on hero image (subtle, desktop only) ────────────
  const heroImage = document.querySelector('.hero__image img');
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDesktop = window.matchMedia('(min-width: 1025px)').matches;

  if (heroImage && !prefersReducedMotion && isDesktop) {
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      if (y < window.innerHeight) {
        heroImage.style.transform = `scale(1.05) translateY(${y * 0.04}px)`;
      }
    }, { passive: true });
  }

  // ── Footer Scroll To Top ──────────────────────────────────
  const scrollTopBtn = document.getElementById('scroll-top-btn');
  if (scrollTopBtn) {
    scrollTopBtn.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });
  }

  // ── Reviews Testimonials Track Navigation ─────────────────
  const reviewsTrack = document.getElementById('testimonials-track');
  const reviewsPrevBtn = document.getElementById('reviews-prev-btn');
  const reviewsNextBtn = document.getElementById('reviews-next-btn');

  if (reviewsTrack && reviewsPrevBtn && reviewsNextBtn) {
    const scrollAmount = 400;

    reviewsPrevBtn.addEventListener('click', () => {
      reviewsTrack.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    });

    reviewsNextBtn.addEventListener('click', () => {
      reviewsTrack.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    });
  }

  // ── Reels Showcase Track Navigation ────────────────────────
  const reelsTrack = document.getElementById('reels-track');
  const reelsPrevBtn = document.getElementById('reels-prev');
  const reelsNextBtn = document.getElementById('reels-next');

  if (reelsTrack && reelsPrevBtn && reelsNextBtn) {
    // Scroll by exactly one card (card width + flex gap) so paging stays aligned.
    const scrollAmount = () => {
      const card = reelsTrack.querySelector('.reel-card');
      const gap = parseFloat(getComputedStyle(reelsTrack).columnGap) || 0;
      return card ? card.offsetWidth + gap : 300;
    };

    reelsPrevBtn.addEventListener('click', () => {
      reelsTrack.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
    });

    reelsNextBtn.addEventListener('click', () => {
      reelsTrack.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
    });
  }

  // ── Reels Video Play/Pause & Audio Unmute ─────────────────
  const reelWraps = document.querySelectorAll('.reel-card__video-wrap');
  reelWraps.forEach(wrap => {
    const video = wrap.querySelector('video');
    if (!video) return;

    // First click: restart this reel from the beginning with sound and hide the play
    // overlay (other reels go quiet). Later clicks on the same reel toggle mute/unmute.
    wrap.addEventListener('click', (e) => {
      if (e.target.closest('.reel-action-btn') || e.target.closest('.reel-card__buy-btn')) return;

      if (!wrap.classList.contains('is-playing')) {
        reelWraps.forEach(other => {
          if (other === wrap) return;
          other.classList.remove('is-playing');
          const ov = other.querySelector('video');
          if (ov) ov.muted = true;
        });
        video.currentTime = 0;
        video.muted = false;
        wrap.classList.add('is-playing');
        video.play().catch(() => {});
      } else {
        video.muted = !video.muted;
        if (video.paused) video.play().catch(() => {});
      }
    });
  });

  // ── Hero Banner Slider Logic ─────────────────────────────
  const heroSlider = document.getElementById('hero-slider');
  if (heroSlider) {
    const slides = heroSlider.querySelectorAll('.hero__slide');
    const btns = heroSlider.querySelectorAll('.hero__slider-btn');
    const autoplay = heroSlider.dataset.autoplay === 'true' && slides.length > 1;
    const interval = Math.max(3000, parseInt(heroSlider.dataset.interval, 10) || 8000);
    let currentSlide = 0;
    let autoSlideTimer = null;

    function goToSlide(index) {
      currentSlide = index;
      slides.forEach((slide, i) => {
        slide.classList.toggle('active', i === index);
      });
      btns.forEach((btn, i) => {
        btn.classList.toggle('active', i === index);
      });
    }

    function startAutoSlide() {
      if (!autoplay) return;
      autoSlideTimer = setInterval(() => {
        const next = (currentSlide + 1) % slides.length;
        goToSlide(next);
      }, interval);
    }

    btns.forEach(btn => {
      btn.addEventListener('click', () => {
        const target = parseInt(btn.getAttribute('data-slide-target'), 10);
        goToSlide(target);
        clearInterval(autoSlideTimer);
        startAutoSlide();
      });
    });

    startAutoSlide();
  }

  // ── How It Works Radial Dial Stage ───────────────────────────
  const radialStage = document.getElementById('how-radial-stage');
  if (radialStage) {
    const stepGroups = radialStage.querySelectorAll('.radial-segment-group');
    const stepOverlays = radialStage.querySelectorAll('.radial-step-overlay');
    const hubScreens = radialStage.querySelectorAll('.radial-hub-screen');
    const mobileStepCards = radialStage.querySelectorAll('.how-mobile-step-card');
    let currentRadialStep = 0;
    let radialTimer = null;

    function goToRadialStep(index) {
      currentRadialStep = index;
      stepGroups.forEach((group, i) => {
        group.classList.toggle('active', i === index);
      });
      stepOverlays.forEach((overlay, i) => {
        overlay.classList.toggle('active', i === index);
      });
      hubScreens.forEach((screen, i) => {
        screen.classList.toggle('active', i === index);
      });
      mobileStepCards.forEach((card, i) => {
        card.classList.toggle('active', i === index);
      });
    }

    function startRadialAutoRotate() {
      radialTimer = setInterval(() => {
        const total = stepGroups.length || hubScreens.length || 3;
        const next = (currentRadialStep + 1) % total;
        goToRadialStep(next);
      }, 4000);
    }

    stepGroups.forEach((group) => {
      group.addEventListener('click', () => {
        const stepIndex = parseInt(group.getAttribute('data-step'), 10);
        goToRadialStep(stepIndex);
        clearInterval(radialTimer);
        startRadialAutoRotate();
      });
    });

    stepOverlays.forEach((overlay) => {
      overlay.addEventListener('click', () => {
        const stepIndex = parseInt(overlay.getAttribute('data-step'), 10);
        goToRadialStep(stepIndex);
        clearInterval(radialTimer);
        startRadialAutoRotate();
      });
    });

    // Mobile interactions: allow tapping the center image hub or step box to advance steps
    const centerHub = radialStage.querySelector('.radial-center-hub');
    if (centerHub) {
      centerHub.addEventListener('click', () => {
        const total = hubScreens.length || 3;
        const next = (currentRadialStep + 1) % total;
        goToRadialStep(next);
        clearInterval(radialTimer);
        startRadialAutoRotate();
      });
    }

    mobileStepCards.forEach((card) => {
      card.addEventListener('click', () => {
        const total = mobileStepCards.length || 3;
        const next = (currentRadialStep + 1) % total;
        goToRadialStep(next);
        clearInterval(radialTimer);
        startRadialAutoRotate();
      });
    });

    startRadialAutoRotate();
  }

  // ── FAQ Accordion Toggle ──────────────────────────────────────
  const faqItems = document.querySelectorAll('.faq-item');
  faqItems.forEach((item) => {
    const btn = item.querySelector('.faq-question-btn');
    const answer = item.querySelector('.faq-answer-wrapper');

    if (btn && answer) {
      btn.addEventListener('click', () => {
        const isOpen = item.classList.contains('open');

        // Close other open FAQ items for single-open experience
        faqItems.forEach((other) => {
          if (other !== item) {
            other.classList.remove('open');
            const otherBtn = other.querySelector('.faq-question-btn');
            const otherAns = other.querySelector('.faq-answer-wrapper');
            if (otherBtn) otherBtn.setAttribute('aria-expanded', 'false');
            if (otherAns) otherAns.style.maxHeight = '0px';
          }
        });

        if (isOpen) {
          item.classList.remove('open');
          btn.setAttribute('aria-expanded', 'false');
          answer.style.maxHeight = '0px';
        } else {
          item.classList.add('open');
          btn.setAttribute('aria-expanded', 'true');
          answer.style.maxHeight = answer.scrollHeight + 'px';
        }
      });
    }
  });

})();
